import {
  CodeItem, SubstanceConversionFactor, SubstanceConversionFactorWire, SubstanceUomSetValue,
  SubstanceUomSetWire,
  UomConversionWire
} from 'app/area/codetable/item/types-store';
import { CodetableMetadata, CodetableFeatures, CodetableAdministrationState } from 'app/area/codetable/types-store';
import { CodeComplexValue } from 'app/area/shared/codetable/types';
import { FilterOption } from 'app/components/options-handler/types';
import { CodetableSelectorProps } from 'app/area/codetable/types';
import { LoadOptions } from 'app/types-shared';
import { CodetableScreenName } from 'app/area/codetable/types-store';
import { CodeScreenTemplateExternalProps } from 'app/area/codetable/item/screen-renderer/screen-template';
import { ValidationProps } from 'ui';

import { Configuration } from 'app/components/metro-line/types';
import { RefObject } from 'react';
import { ValidationData } from 'app/area/codetable/item/validation/validate';
import { ScreenPicker, ScreenPickerProps } from 'app/area/codetable/item/screen-renderer/types';

export interface CodeLanguageItemWire {
  v: number;
  d?: string;
  ld?: string;
}

export interface CodeItemValueWire {
  v: number;
  d?: string;
  ld?: string;
  decimalColour?: number;
  disabled?: string;
  readOnly?: boolean;
}

export interface CodeItemWire {
  disabled: string;
  decimalColour: number;
  icon: string;
  icon2: string;
  sequence: number;
  colourCode: CodeItemValueWire;
  code: CodeItemValueWire;
  parent: CodeItemValueWire;
  node: CodeItemValueWire;
  category: CodeItemValueWire;
  language: CodeLanguageItemWire[];
  exclude: CodeItemValueWire[];
  subset: CodeItemValueWire[];
  application: CodeItemValueWire[];
  class: CodeItemValueWire[];
  companyRegisterId?: string;
  scoreCalcWeight?: number;
  severity?: CodeItemValueWire;
  uom?: CodeItemValueWire;
  low?: number;
  mid?: number;
  high?: number;
  recurrence?: CodeItemValueWire;
  likelihood?: CodeItemValueWire;
  minLevel?: number;
  level?: number;
  checkedCode: CodeItemValueWire;
  uncheckedCode: CodeItemValueWire;
  rangeTo?: number;
  rangeFrom?: number;
  defaultUom?: CodeItemValueWire;
  defaultUomType?: CodeItemValueWire;
  substanceUomSet?: SubstanceUomSetWire[];
  substanceCategoryUomSet?: SubstanceUomSetWire[];
  substanceConversionFactor?: SubstanceConversionFactorWire[];
  consequenceType?: CodeItemValueWire;
  uomTypeFrom?: CodeItemValueWire;
  uomTypeTo?: CodeItemValueWire;
  measurementSystem?: CodeItemValueWire;
  uomConversion?: UomConversionWire[];
  descendantCount?: number;
}

export interface CodeItemSubsetWire {
  subset: CodeItemValueWire[];
}

export interface SaveCodetableItem {
  codetableId: number;
  breadcrumbs: number[];
  metadata: CodetableMetadata;
  options: { [key: string]: FilterOption };
  validationErrorMessage: string;
  mainLanguage: CodeComplexValue<number>;
  actionType: CodeActionType;
  saveSubsetsOnly: boolean;
  substanceUomSetType: SubstanceUomSetType;
  isUomExcluded: boolean;
  isHierarchy: boolean;
  withDescendants?: boolean;
}

export interface CopyTranslationsPayload {
  codeItem: CodeItem;
  mainLanguage: CodeComplexValue<number>;
  languageOptions: CodeComplexValue<number>[];
  features: CodetableFeatures;
  successText: string;
}

export interface CodeToWire {
  codetableId: number;
  codeItem: CodeItem;
  languageOptions: CodeComplexValue<number>[];
  features: CodetableFeatures;
  substanceUomSetType?: SubstanceUomSetType;
  withDescendants?: boolean;
}

export interface CreateEmptyCodeItemPayload {
  metadata: CodetableMetadata;
  caseTypes: FilterOption;
  userGroups: FilterOption;
  languages: FilterOption;
  parent: CodeComplexValue<number>;
  category: CodeComplexValue<number>;
  parentId: number;
  substanceUomSet: SubstanceUomSetValue[];
}

export type LoadCodetableOptions = (id: string, codetableId: number, loadOptionsParams: LoadOptions) => any;

export type LoadCodeOptions = (id: string, componentEndpoint: string, loadOptionsParams: LoadOptions, optionsParams?: any) => void;

export type LoadCodeItem = (payload: CodetableSelectorProps) => void;

export type CreateCodeItem = (payload: CodetableSelectorProps) => void;

export interface CreateCodeItemProps extends CodetableSelectorProps {
  loadCodeItem: LoadCodeItem;
  createCodeItem: CreateCodeItem;
  loadOptions: LoadCodeOptions;
  loadCodetableOptions: LoadCodetableOptions;
  locale: string;
}

export interface InitializeCodeItemPayload extends CodetableAdministrationState, CodetableSelectorProps {
  breadcrumbs: number[];
  codeId: number;
  parentId: number;
  categoryId: number;
  section: string;
  actionType: CodeActionType;
  locale: string;
  codetableId: number;

  createCodeItem: CreateCodeItem;
  loadCodeItem: LoadCodeItem;
  loadOptions: (id: string, componentEndpoint: string, loadOptionsParams: LoadOptions, optionsParams?: any) => void;
  loadCodetableOptions: LoadCodetableOptions;
  codetableBlockAccess: (breadcrumbs: number[]) => void;
  clearValidation: () => void;

  options: { [key: string]: FilterOption };
}

export enum CodeEditSectionType {
  Details = 'details',
  Visibility = 'visibility',
  Translations = 'translations',
  Types = 'types',
  ScalarValues = 'scalarvalues',
  UnitOfMeasureDetails = 'unitOfMeasureDetails',
  UnitOfMeasureSets = 'unitOfMeasureSets',
  SubstanceLinks = 'substanceLinks',
  ConversionFactors = 'conversionFactors',
  UomConversion = 'uomConversion',
  RiskMatrixReminderSection = 'riskMatrixReminderSection'
}

export enum CodeActionType {
  Add = 'add',
  Edit = 'edit',
  Duplicate = 'duplicate'
}

export enum ReadOnlyType {
  all = 'all',
  save = 'save',
  subsets = 'subsets',
  translations = 'translations',
  codeParent = 'codeParent'
}

export enum SubstanceUomSetType {
  substanceUomSet = 'substanceUomSet',
  substanceCategoryUomSet = 'substanceCategoryUomSet'
}

export type ScreenSlots = {
  [key in CodeEditSectionType]?: Slot;
};

export interface Slot {
  top?: any;
  bottom?: any;
  [key: string]: any;
}

export interface SlotProps {
  sectionSlots?: Slot;
}

export interface CodetableScreen extends CodetableScreenProps {
  id: number;
  name: CodetableScreenName;
  component: React.ComponentClass<CodeScreenTemplateExternalProps>;
  screenSlots?: ScreenSlots;
  screenConfiguration?: (payload: MetroLineConfigurationPayload) => Configuration<CodeEditSectionType>[];
  validate?: CodeScreenValidate;
}

export interface CodetableScreenProps {
  screenProps?: { [key: string]: any };
}

export interface MetroLineConfigurationPayload {
  detailsSectionRef: RefObject<HTMLDivElement>;
  visibilitySectionRef: RefObject<HTMLDivElement>;
  translationsSectionRef: RefObject<HTMLDivElement>;
  unitOfMeasureDetailsSectionRef: RefObject<HTMLDivElement>;
  unitOfMeasureSetsSectionRef: RefObject<HTMLDivElement>;
  substanceLinksSectionRef: RefObject<HTMLDivElement>;
  riskMatrixConfigurationRef: RefObject<HTMLDivElement>;
  conversionFactorsSectionRef: RefObject<HTMLDivElement>;
  uomConversionSectionRef: RefObject<HTMLDivElement>;
  typesRef: RefObject<HTMLDivElement>;
  scalarValuesRef: RefObject<HTMLDivElement>;
  breadcrumbs: number[];
  codeId: number;
  metadata: CodetableMetadata;
  validationErrors: { [key: string]: boolean };
  actionType: CodeActionType;
  supportedScreens: CodetableScreen[];
}

export interface CodeItemCodesAccess {
  id: number;
  edit: boolean;
  move: boolean;
  delete: boolean;
  readonlySubsets?: number[];
}

export interface ReadOnlyConfigurationValue {
  codeId?: number;
  actionType?: CodeActionType;
  canMove?: boolean;
  canCreate?: boolean;
  administrationCentral?: boolean;
  canEdit?: boolean;
  readonlySubsets?: number[];
  codetableMetadata?: CodetableMetadata;
}

export type ReadOnlyConfiguration = { [key: string]: boolean };

export type CodeScreenValidate = (validationData: ValidationData) => { [key: string]: ValidationProps };

export interface UomSetProps extends ScreenPickerProps {
  substanceUomSetValue: SubstanceUomSetValue;
}

export interface CodeItemAccessConfiguration {
  canCreate: boolean;
  canDuplicate: boolean;
  canDelete: boolean;
  isCentralAdmin: boolean;
}

export interface UpdateConversionFactorPayload {
  value: CodeComplexValue<number> | number;
  key: keyof SubstanceConversionFactor;
  codeItem: CodeItem;
  conversionFactor: SubstanceConversionFactor;
  validation: { [key: string]: ValidationProps };
}

export interface UomSetValidation {
  uomSet: number;
  uomTypeName: ScreenPicker;
  uomTypeValue: number;
  validation: ValidationProps;
}
